import { useEffect, useState, useCallback, createContext, useContext } from 'react';

// Contexto para compartir el estado de versión con otros componentes
export const VersionContext = createContext({
  currentVersion: null,
  serverVersion: null,
  updateAvailable: false,
  lastChecked: null,
  checkNow: () => {},
  applyUpdate: () => {}
});

// Hook para usar el contexto de versión
export const useVersion = () => useContext(VersionContext);

// Provider del contexto de versión - VERSIÓN MEJORADA para móviles
export const VersionProvider = ({ children }) => {
  const [currentVersion, setCurrentVersion] = useState(() => {
    try {
      return localStorage.getItem('app_version') || null;
    } catch {
      return null;
    }
  });
  const [serverVersion, setServerVersion] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  // Función para verificar la versión - Con bypass de caché agresivo
  const checkVersion = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      // URL con múltiples parámetros anti-caché
      const cacheBuster = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const versionUrl = `/version.json?_=${cacheBuster}&nocache=true`;
      
      const response = await fetch(versionUrl, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.log('VersionChecker: version.json no disponible');
        return;
      }
      
      const data = await response.json();
      const newServerVersion = data?.version;
      
      if (!newServerVersion) return;
      
      setServerVersion(newServerVersion);
      setLastChecked(new Date());
      
      // Obtener versión local
      let localVersion = null;
      try {
        localVersion = localStorage.getItem('app_version');
      } catch {}
      
      // Primera vez - guardar y salir
      if (!localVersion) {
        try {
          localStorage.setItem('app_version', newServerVersion);
        } catch {}
        setCurrentVersion(newServerVersion);
        console.log(`VersionChecker: Primera vez, guardando versión ${newServerVersion}`);
        return;
      }
      
      setCurrentVersion(localVersion);
      
      // Detectar si hay actualización disponible
      if (newServerVersion !== localVersion) {
        console.log(`🔄 Nueva versión disponible: ${localVersion} → ${newServerVersion}`);
        setUpdateAvailable(true);
      } else {
        setUpdateAvailable(false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.log('VersionChecker: verificación omitida');
      }
    }
  }, []);

  // Función para aplicar la actualización - Limpieza agresiva de caché
  const applyUpdate = useCallback(async () => {
    if (!serverVersion) return;
    
    try {
      // Guardar nueva versión ANTES de recargar
      try {
        localStorage.setItem('app_version', serverVersion);
      } catch {}
      
      // Limpiar Service Workers
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(r => r.unregister()));
          console.log('✅ Service Workers eliminados');
        } catch (e) {
          console.log('Service Workers: ', e.message);
        }
      }
      
      // Limpiar todos los caches del navegador
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          console.log('✅ Caches eliminados:', cacheNames.length);
        } catch (e) {
          console.log('Caches: ', e.message);
        }
      }
      
      // Forzar recarga completa ignorando caché
      // En móviles, usar location.href con parámetro anti-caché
      const newUrl = window.location.pathname + '?_refresh=' + Date.now();
      window.location.replace(newUrl);
      
    } catch (error) {
      console.error('Error aplicando actualización:', error);
      // Fallback: recarga simple
      window.location.reload(true);
    }
  }, [serverVersion]);

  // Verificar versión al montar y periódicamente
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Verificar inmediatamente después de 3 segundos
    const initialCheck = setTimeout(checkVersion, 3000);
    
    // Verificar cada 5 minutos
    const interval = setInterval(checkVersion, 5 * 60 * 1000);

    // Verificar cuando el usuario vuelve al app (clave en PWA mobile)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    const handleFocus = () => checkVersion();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearTimeout(initialCheck);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkVersion]);

  return (
    <VersionContext.Provider value={{
      currentVersion,
      serverVersion,
      updateAvailable,
      lastChecked,
      checkNow: checkVersion,
      applyUpdate
    }}>
      {children}
    </VersionContext.Provider>
  );
};

// Componente para mostrar la versión con indicador de actualización
export const VersionDisplay = ({ className = '' }) => {
  const { currentVersion, serverVersion, updateAvailable, applyUpdate, checkNow } = useVersion();
  
  // Formatear versión para mostrar
  const formatVersion = (version) => {
    if (!version || version.length < 8) return version || 'v1.0';
    
    try {
      const year = version.substring(0, 4);
      const month = version.substring(4, 6);
      const day = version.substring(6, 8);
      const hour = version.substring(8, 10) || '00';
      const min = version.substring(10, 12) || '00';
      
      return `v${day}/${month}/${year.slice(2)} ${hour}:${min}`;
    } catch {
      return version;
    }
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button
        onClick={checkNow}
        className="text-xs font-mono text-gray-600 bg-blue-50 px-1.5 py-0.5 rounded hover:bg-blue-100 transition-colors"
        title="Click para verificar actualizaciones"
      >
        {formatVersion(currentVersion)}
      </button>
      {updateAvailable && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            applyUpdate();
          }}
          className="relative group flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full animate-pulse hover:bg-red-600 transition-colors"
          title={`Nueva versión: ${formatVersion(serverVersion)}`}
        >
          {/* Punto rojo pulsante */}
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
          </span>
          <span className="font-medium">Actualizar</span>
        </button>
      )}
    </div>
  );
};

// Componente legacy
const VersionChecker = () => null;

export default VersionChecker;
